import { FC, useContext, useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { MobXProviderContext } from "mobx-react";
import { Toast } from "primereact/toast";
import { Chart } from 'primereact/chart';
import { Dropdown } from "primereact/dropdown";
import { getErrorTitle } from "../../../constants/constants";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appInnerCardStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  appNeutralPanelStyle,
  appWideDialogStyle,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { useResponsive } from "../../../responsive/ResponsiveProvider";

interface WebsiteStatisticsModalProps {
  homeId: string | undefined;
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
}

const WebsiteStatisticsModal: FC<WebsiteStatisticsModalProps> = ({
  homeId,
  isOpen,
  toggleModal,
}) => {
  const toast = useRef<Toast>(null);
  const mobile = useResponsive().isMobile;
  const { backendAPIStore } = useContext(MobXProviderContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [trafficChartData, setTrafficChartData] = useState<any | null>(null);
  const [engagementChartData, setEngagementChartData] = useState<any | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<string>("7D");
  const [summary, setSummary] = useState({
    pageViews: 0,
    uniqueVisitors: 0,
    inquiries: 0,
    requestInfoOpens: 0,
    conversionRate: 0,
  });
  const [topPages, setTopPages] = useState<Array<{ page: string; views: number }>>([]);
  const [actionTotals, setActionTotals] = useState<Array<{ action: string; count: number }>>([]);

  const chartOptions = {
    maintainAspectRatio: false,
    aspectRatio: 1.8,
    plugins: {
      legend: {
        labels: {
          color: "#334155",
          boxWidth: 14,
          boxHeight: 14,
          useBorderRadius: true,
          borderRadius: 8,
          padding: 18,
          font: {
            size: 13,
            weight: 600,
          },
        },
      },
      tooltip: {
        backgroundColor: "#0f172a",
        titleColor: "#ffffff",
        bodyColor: "#e2e8f0",
        padding: 12,
        cornerRadius: 12,
        displayColors: false,
      }
    },
    scales: {
      x: {
        ticks: {
          color: "#64748b",
          maxRotation: 0,
          autoSkip: true,
          font: {
            size: 11,
            weight: 500,
          },
        },
        grid: {
          display: false,
        },
        border: {
          display: false,
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: "#64748b",
          precision: 0,
          font: {
            size: 11,
          },
        },
        grid: {
          color: "#e2e8f0",
          drawBorder: false,
        },
        border: {
          display: false,
        }
      }
    }
  };

  const metricCardStyle = {
    ...appInnerCardStyle,
    height: "100%",
  } as const;

  const prettifyPageLabel = (page: string) => {
    if (!page || page === "home") {
      return "Home";
    }
    return page
      .split("-")
      .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
      .join(" ");
  };

  const prettifyActionLabel = (action: string) =>
    action
      .toLowerCase()
      .split("_")
      .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
      .join(" ");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getWebsiteMetrics(homeId, selectedDuration);
        const metricData = response.metricData || {};
        const labels = (metricData.dates || []).map((timestamp: string) => new Date(timestamp).toLocaleDateString());
        const pageLoadCounts = metricData.pageLoadCounts || [];
        const uniqueVisitors = metricData.uniqueVisitors || [];
        const inquiryCounts = metricData.inquiryCounts || [];
        const requestInfoOpenCounts = metricData.requestInfoOpenCounts || [];

        setTrafficChartData({
          labels,
          datasets: [
            {
              label: 'Page Views',
              data: pageLoadCounts,
              fill: true,
              borderColor: "#f59e0b",
              backgroundColor: (context) => {
                const chart = context.chart;
                const { ctx, chartArea } = chart;
                if (!chartArea) return "rgba(245, 158, 11, 0.16)";
                const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                gradient.addColorStop(0, "rgba(245, 158, 11, 0.35)");
                gradient.addColorStop(1, "rgba(245, 158, 11, 0.04)");
                return gradient;
              },
              tension: 0.42,
              borderWidth: 3,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: "#ffffff",
              pointBorderColor: "#f59e0b",
              pointBorderWidth: 2,
            },
            {
              label: 'Unique Visitors',
              data: uniqueVisitors,
              fill: false,
              borderColor: "#185DA2",
              backgroundColor: "#185DA2",
              tension: 0.38,
              borderWidth: 3,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: "#ffffff",
              pointBorderColor: "#185DA2",
              pointBorderWidth: 2,
            },
          ]
        });

        setEngagementChartData({
          labels,
          datasets: [
            {
              label: "Inquiries",
              data: inquiryCounts,
              backgroundColor: "#f59e0b",
              borderRadius: 10,
            },
            {
              label: "Request Info Opens",
              data: requestInfoOpenCounts,
              backgroundColor: "#185DA2",
              borderRadius: 10,
            },
          ],
        });

        setSummary({
          pageViews: Number(metricData.totals?.pageViews || 0),
          uniqueVisitors: Number(metricData.totals?.uniqueVisitors || 0),
          inquiries: Number(metricData.totals?.inquiries || 0),
          requestInfoOpens: Number(metricData.totals?.requestInfoOpens || 0),
          conversionRate: Number(metricData.totals?.conversionRate || 0),
        });
        setTopPages(metricData.topPages || []);
        setActionTotals(metricData.actionTotals || []);
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
        console.error("Error fetching website metrics:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [backendAPIStore, homeId, selectedDuration]);

  const headerElement = renderSurfaceHeader(
    "Website Statistics",
    "Review traffic, visitor trends, inquiries, and engagement across the public site.",
    "pi pi-chart-line",
  );

  return (
    <Dialog visible={isOpen} modal header={headerElement} style={mobile ? { width: '100%' } : appWideDialogStyle}
      headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle}
      onHide={() => { if (!isOpen) return; toggleModal(false); }}>
      <Toast ref={toast} />
      <div style={appAccentPanelStyle} className="mb-3">
        <div style={{ fontWeight: 700, color: "#9a3412" }}>Website performance</div>
        <div className="mt-2" style={appMutedTextStyle}>
          Compare traffic, visitor interest, and inquiry activity across recent time windows.
        </div>
      </div>
      <div className="grid mb-3">
        <div className="col-12 md:col-6 xl:col-3">
          <div style={metricCardStyle}>
            <div style={{ fontSize: "0.8rem", letterSpacing: "0.04em", textTransform: "uppercase", color: "#64748b" }}>Page Views</div>
            <div className="mt-2" style={{ fontSize: "1.8rem", fontWeight: 700, color: "#0f172a" }}>{summary.pageViews}</div>
          </div>
        </div>
        <div className="col-12 md:col-6 xl:col-3">
          <div style={metricCardStyle}>
            <div style={{ fontSize: "0.8rem", letterSpacing: "0.04em", textTransform: "uppercase", color: "#64748b" }}>Unique Visitors</div>
            <div className="mt-2" style={{ fontSize: "1.8rem", fontWeight: 700, color: "#0f172a" }}>{summary.uniqueVisitors}</div>
          </div>
        </div>
        <div className="col-12 md:col-6 xl:col-3">
          <div style={metricCardStyle}>
            <div style={{ fontSize: "0.8rem", letterSpacing: "0.04em", textTransform: "uppercase", color: "#64748b" }}>Inquiries</div>
            <div className="mt-2" style={{ fontSize: "1.8rem", fontWeight: 700, color: "#0f172a" }}>{summary.inquiries}</div>
          </div>
        </div>
        <div className="col-12 md:col-6 xl:col-3">
          <div style={metricCardStyle}>
            <div style={{ fontSize: "0.8rem", letterSpacing: "0.04em", textTransform: "uppercase", color: "#64748b" }}>Conversion Rate</div>
            <div className="mt-2" style={{ fontSize: "1.8rem", fontWeight: 700, color: "#0f172a" }}>{summary.conversionRate}%</div>
          </div>
        </div>
        <div className="col-12 md:col-6">
          <div style={metricCardStyle}>
            <div style={{ fontSize: "0.8rem", letterSpacing: "0.04em", textTransform: "uppercase", color: "#64748b" }}>Request Info Opens</div>
            <div className="mt-2" style={{ fontSize: "1.6rem", fontWeight: 700, color: "#185DA2" }}>{summary.requestInfoOpens}</div>
            <div className="mt-2" style={{ color: "#64748b", lineHeight: 1.7 }}>A useful signal for visitor intent before they submit an inquiry.</div>
          </div>
        </div>
        <div className="col-12 md:col-6">
          <div style={metricCardStyle}>
            <div style={{ fontSize: "0.8rem", letterSpacing: "0.04em", textTransform: "uppercase", color: "#64748b" }}>Selected Window</div>
            <div className="mt-2" style={{ fontSize: "1.6rem", fontWeight: 700, color: "#0f172a" }}>{selectedDuration}</div>
            <div className="mt-2" style={{ color: "#64748b", lineHeight: 1.7 }}>Switch between time ranges to compare traffic and conversion patterns.</div>
          </div>
        </div>
      </div>
      <div style={appFormSectionStyle}>
        <div className="mb-3">
          <Dropdown options={[
            { label: 'Last 7 Days', value: '7D' },
            { label: 'Last 30 Days', value: '30D' },
            { label: 'Last 90 Days', value: '90D' },
          ]} placeholder="Select duration" value={selectedDuration} onChange={(e) => setSelectedDuration(e.value)} />
        </div>
        <div className="grid">
          <div className="col-12 xl:col-8">
            <div style={{ ...appNeutralPanelStyle, minHeight: "380px", padding: "1rem 1.25rem" }}>
              <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.85rem" }}>Traffic Trend</div>
              <Chart type="line" data={trafficChartData} options={chartOptions} />
            </div>
          </div>
          <div className="col-12 xl:col-4">
            <div style={{ ...appNeutralPanelStyle, minHeight: "380px", padding: "1rem 1.25rem" }}>
              <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.85rem" }}>Top Pages</div>
              <div className="flex flex-column gap-2">
                {topPages.length > 0 ? topPages.map((item) => (
                  <div key={item.page} style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "16px", padding: "0.85rem 0.95rem" }} className="flex justify-content-between align-items-center gap-3">
                    <div>
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>{prettifyPageLabel(item.page)}</div>
                      <div style={{ color: "#64748b", fontSize: "0.88rem", marginTop: "0.2rem" }}>{item.page}</div>
                    </div>
                    <div style={{ color: "#185DA2", fontWeight: 800 }}>{item.views}</div>
                  </div>
                )) : (
                  <div style={{ color: "#64748b" }}>No page-level traffic yet for this time window.</div>
                )}
              </div>
            </div>
          </div>
          <div className="col-12 xl:col-8">
            <div style={{ ...appNeutralPanelStyle, minHeight: "360px", padding: "1rem 1.25rem" }}>
              <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.85rem" }}>Inquiry And CTA Trend</div>
              <Chart type="bar" data={engagementChartData} options={chartOptions} />
            </div>
          </div>
          <div className="col-12 xl:col-4">
            <div style={{ ...appNeutralPanelStyle, minHeight: "360px", padding: "1rem 1.25rem" }}>
              <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.85rem" }}>Engagement Actions</div>
              <div className="flex flex-column gap-2">
                {actionTotals.length > 0 ? actionTotals.map((item) => (
                  <div key={item.action} style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "16px", padding: "0.85rem 0.95rem" }} className="flex justify-content-between align-items-center gap-3">
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>{prettifyActionLabel(item.action)}</div>
                    <div style={{ color: "#b45309", fontWeight: 800 }}>{item.count}</div>
                  </div>
                )) : (
                  <div style={{ color: "#64748b" }}>No tracked engagement actions yet for this time window.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default WebsiteStatisticsModal;
